import {assert} from 'Asserts';
import deepCopy from 'lib/deepCopy';
import {ConfigShapePath} from 'pipelines/generalized-step-config-editor/StepConfigEditorTypes';
import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import {JSONValue, isJSONArray, isJSONObject} from 'util/Types';

export function getConfigShapeAtConfigShapePath(
  configShapePath: ConfigShapePath,
  configShape: PipelineConfigJsonSpec,
  config: Record<string, JSONValue> | null
): PipelineConfigJsonSpec {
  return configShapePath.reduce((narrowedConfigShape, configKey, currentIndex) => {
    // Handle array indices
    if (typeof configKey === 'number') {
      assert(narrowedConfigShape.kind === 'array', 'Numeric path segment requires array shape');
      return narrowedConfigShape.elementSpec;
    }
    // Handle object keys
    assert(
      narrowedConfigShape.kind === 'shape' && narrowedConfigShape.fields.hasOwnProperty(configKey),
      'Invalid path for config shape',
      {configShapePath, configShape, config}
    );
    if (
      narrowedConfigShape.fields[configKey].kind === 'taggedUnion' &&
      currentIndex !== configShapePath.length - 1
    ) {
      // Choose which configShape based on the current value of the tagged union in the config object
      const tagKey = narrowedConfigShape.fields[configKey].tagKey;
      const tagValue = maybeGetValueFromConfig(config, [
        ...configShapePath.slice(0, currentIndex + 1),
        tagKey,
      ]);
      let choice: PipelineConfigJsonSpec | undefined;
      if (tagValue == null) {
        // default to the first choice
        choice = narrowedConfigShape.fields[configKey].choices[0];
      } else {
        choice = narrowedConfigShape.fields[configKey].choices.find(
          (choice) => choice.fields[tagKey].kind === 'string' && choice.fields[tagKey].value === tagValue
        );
      }

      assert(choice != null, 'No choice found for tagged union', {
        configShapePath: configShapePath.join('.'),
        configKey,
        tagValue,
      });
      return choice;
    }
    // TODO(BNCH-120657): Update path to work for arrays
    return narrowedConfigShape.fields[configKey];
  }, configShape);
}

/**
 * Given a config and a config shape path, return the value at the path.
 * @param config - The config object.
 * @param configShapePath - The path to the value.
 * @returns The value at the path, or null if the path is invalid.
 *
 * With the addition of array editor, the path can be a mix of numbers and strings. So we need to handle
 * both cases. If it's a number key, the lookup will be an array index (ex: config = ['foo', 'bar']; config[1] = 'bar'). So if it's a number key, but the
 * config is not an array, exit. Ex: config = {foo: 'bar'}; config[1] is invalid.
 *
 * If it's a string key, we must lookup in an object (ex: config = {foo: 'bar'}; config['foo'] = 'bar'). So if it's a string key, but the
 * config is not an object or IS an array, exit. Ex: config = ['foo', 'bar']; config['foo'] is invalid.
 */
export function maybeGetValueFromConfig(
  config: Record<string, JSONValue> | null,
  configShapePath: ConfigShapePath
): JSONValue | null {
  let current: JSONValue = deepCopy(config);
  for (const key of configShapePath) {
    if (current == null) {
      // For draft steps config is empty so we return null
      return null;
    }

    if (typeof key === 'number') {
      if (!isJSONArray(current)) {
        return null;
      }
      current = current[key];
    } else {
      if (typeof current !== 'object' || isJSONArray(current)) {
        return null;
      }
      current = current[key];
    }
  }
  return current;
}

export function setValueInConfig(
  config: Record<string, JSONValue>,
  configShapePath: ConfigShapePath,
  newValue: JSONValue
): Record<string, JSONValue> {
  if (configShapePath.length === 0) {
    assert(isJSONObject(newValue), 'New value must be an object if path is empty');
    return newValue;
  }
  const configCopy = deepCopy(config);
  const numericErrorString = 'Numeric key requires array';
  const objectErrorString = 'Cannot set value at configShapePath: parent is not an object';

  let current: JSONValue = configCopy;
  for (let i = 0; i < configShapePath.length - 1; i++) {
    const key = configShapePath[i];

    if (typeof key === 'number') {
      assert(isJSONArray(current), numericErrorString);
      current = current[key];
    } else {
      assert(isJSONObject(current), objectErrorString, {
        configShapePath: configShapePath.join('.'),
      });
      current = current[key];
    }
  }

  const finalKey = configShapePath[configShapePath.length - 1];
  if (typeof finalKey === 'number') {
    assert(isJSONArray(current), numericErrorString);
    current[finalKey] = newValue;
  } else {
    assert(isJSONObject(current), objectErrorString, {
      configShapePath: configShapePath.join('.'),
    });
    current[finalKey] = newValue;
  }
  return configCopy;
}
